import tinycolor from "tinycolor2";
import { blendColors } from "../utilities/utilities";
import { app } from "js/namespaces";
import { DecorationStyle } from "../../../common/constants";

export class StyleSet {
    constructor(styleSet) {
        // Object.assign(this, styleSet);
        this._styleSet = styleSet;
    }

    get id() {
        return this._styleSet.id;
    }

    get name() {
        return this._styleSet.name;
    }

    get primary() {
        return color(this._styleSet.primary ?? "black");
    }

    get secondary() {
        return color(this._styleSet.secondary ?? "black");
    }

    get hyperlink() {
        return color(this._styleSet.hyperlink ?? "blue");
    }

    get calloutStyleSet() {
        return this._styleSet.calloutStyleSet ?? "light";
    }

    get colors() {
        return this._styleSet.colors ?? [];
    }

    get containers() {
        return this._styleSet.containers ?? [];
    }

    get chartColors() {
        return this._styleSet.chartColors ?? [];
    }

    get collectionColors() {
        return this._styleSet.collectionColors ?? [];
    }

    get shapeStyles() {
        return this._styleSet.shapeStyles ?? {
            ...getDefaultShapeStyle(DecorationStyle.FILLED),
            defaultColor: "color-0"
        };
    }

    get containerStyles() {
        return this._styleSet.containerStyles ?? {
            ...getDefaultShapeStyle(DecorationStyle.FILLED),
            defaultColor: "container-0"
        };
    }

    get iconStyles() {
        return this._styleSet.iconStyles ?? {
            ...getDefaultShapeStyle(DecorationStyle.FILLED),
            defaultColor: "color-0"
        };
    }

    getColor(colorId, itemIndex, itemCount) {
        colorId = migrateLegacyColor(this, colorId);

        if (!colorId || colorId == "none" || colorId == "transparent") {
            return color("none");
        }

        if (colorId.hasOwnProperty("value")) {
            // custom color support - return the raw color object
            return color(colorId);
        }
        if (colorId.startsWith("collection")) {
            let colors = this.getCollectionColorColors(colorId, itemCount);
            return colors[itemIndex % colors.length];
        }
        if (colorId.contains("-to-")) {
            let colors = this.getShadedCollectionColors(colorId, itemCount);
            return colors[itemIndex % colors.length];
        }

        return this.resolveColorId(colorId);
    }

    resolveColorId(colorId) {
        if (typeof colorId == "string") {
            colorId = migrateLegacyColor(this, colorId);

            if (colorId == "none") {
                return color("none");
            }
            if (colorId == "light" || colorId == "dark") {
                return {
                    type: "shade",
                    value: colorId
                };
            }

            if (this._styleSet.hasOwnProperty(colorId)) {
                // named colors like primary, secondary, hyperlink, positive, negative
                return color(this._styleSet[colorId]);
            }

            if (colorId.startsWith("color-") && this.colors.length > 0) {
                return color(this.colors[parseInt(colorId.split("-")[1]) % this.colors.length]);
            } else if (colorId.startsWith("container-") && this.containers.length > 0) {
                return color(this.containers[parseInt(colorId.split("-")[1]) % this.containers.length]);
            } else if (colorId.startsWith("chart-") && this.chartColors.length > 0) {
                return color(this.chartColors[parseInt(colorId.split("-")[1]) % this.chartColors.length]);
            } else if (colorId.startsWith("rgb") || colorId.startsWith("#")) {
                return color(colorId);
            } else {
                console.warn("Color not found", colorId);
                return color(colorId);
            }
        } else {
            console.warn("Color invalid format", colorId);
            return color("red");
        }
    }

    getCollectionColor(collectionColorId) {
        let index;
        if (collectionColorId && collectionColorId.startsWith("collection-")) {
            index = parseInt(collectionColorId.split("-")[1]);
        } else {
            index = 0;
        }
        return this.collectionColors[index % this.collectionColors.length];
    }

    getCollectionColorColors(collectionColorId, count) {
        let collectionColor;
        if (typeof collectionColorId == "string") {
            collectionColor = this.getCollectionColor(collectionColorId);
        } else {
            collectionColor = collectionColorId;
        }

        if (!collectionColor) {
            return ["color-0"];
        }

        let colors = [];
        if (collectionColor.type === "sequence") {
            if (count === undefined) {
                count = collectionColor.colors.length;
            }
            for (let i = 0; i < count; i++) {
                colors.push(this.resolveColorId(collectionColor.colors[i % collectionColor.colors.length]));
            }
        }
        if (collectionColor.type === "gradient") {
            if (count === undefined) {
                count = 5;
            }
            for (let i = 0; i < count; i++) {
                colors.push(getShadedColor(
                    this.resolveColorId(collectionColor.startColor).value,
                    this.resolveColorId(collectionColor.endColor).value
                    , i, count
                ));
            }
        }
        return colors;
    }

    getColorFromCollectionColor(collectionColor, index, maxCount) {
        if (!collectionColor) {
            console.warn("Collection color not found", collectionColor);
            return "color-0";
        }
        if (collectionColor.type === "sequence") {
            return collectionColor.colors[index % collectionColor.colors.length];
        }
        if (collectionColor.type === "gradient") {
            return getShadedColor(
                this.resolveColorId(collectionColor.startColor).value,
                this.resolveColorId(collectionColor.endColor).value
                , index, maxCount
            );
        }

        return "color-0";
    }

    getShadedCollectionColors(shadedColor, count) {
        let colorIds = shadedColor.split("-to-");

        let colors = [];
        for (let i = 0; i < count; i++) {
            colors.push(getShadedColor(
                this.resolveColorId(colorIds[0]).value,
                this.resolveColorId(colorIds[1]).value
                , i, count
            ));
        }
        return colors;
    }

    getRenderStyles(colorId, userStyleOverride, styles = {}) {
        let color;

        if (colorId.hasOwnProperty("type")) {
            color = colorId;
        } else {
            color = this.resolveColorId(colorId);
        }

        if (userStyleOverride) {
            styles = getDefaultShapeStyle(userStyleOverride);
        }

        if (color.type == "none" || colorId == "none" || colorId == "transparent" || userStyleOverride == "none") {
            styles = {
                fillOpacity: 0,
                strokeOpacity: 0
            };
        }

        let fillColor, strokeColor;

        if (styles.fillColor) {
            fillColor = this.getColor(styles.fillColor);
        } else if (color.value == "default") {
            fillColor = this.getColor(styles.defaultColor);
        } else {
            fillColor = color;
        }

        if (styles.strokeColor) {
            strokeColor = this.getColor(styles.strokeColor);
        } else if (color.value == "default") {
            strokeColor = this.getColor(styles.defaultColor);
        } else {
            strokeColor = color;
        }

        // strokeColor = this.getColor(styles?.strokeColor ?? colorId);

        let foregroundStyleSet;
        // if the fill is transparent, return the current styleset, otherwise return the color's styleset
        if (styles.fillOpacity > 0) {
            foregroundStyleSet = color.styleSet ?? tinycolor(color.value).isDark() ? "dark" : "light";
        } else {
            foregroundStyleSet = this.id;
        }

        return {
            ...styles,
            color,
            fillColor,
            strokeColor,
            fillOpacity: styles.fillOpacity ?? 1,
            strokeOpacity: styles.strokeWidth > 0 ? 1 : 0, // shapeStyle.strokeOpacity ?? 0,
            strokeWidth: styles.strokeWidth ?? 0,
            // foregroundStyleSet,
            // background: color,
            shadow: styles.shadow
        };
    }
}

export class ColorObj {
    constructor(props) {
        if (typeof props == "string") {
            if (props == "none") {
                props = {
                    type: "none",
                    value: "transparent",
                    styleSet: null
                };
            } else {
                props = {
                    type: "color",
                    value: props,
                    styleSet: tinycolor(props).isDark() ? "color-dark" : "color=light"
                };
            }
        }

        this.type = props.type || "color";
        this.value = props.value;
        this._styleSet = props.styleSet;
    }

    get styleSet() {
        if (this._styleSet) {
            return this._styleSet;
        } else {
            if (this.type == "color") {
                return tinycolor(this.value).isDark() ? "dark" : "light";
            } else {
                return "dark";
            }
        }
    }

    isDark() {
        return tinycolor(this.value).isDark();
    }

    _adjustColor(action, value) {
        if (this.type == "color") {
            return new ColorObj({
                type: "color",
                value: tinycolor(this.value)[action](value).toRgbString(),
                styleSet: this.styleSet
            });
        } else if (this.type == "gradient") {
            // adjust color on gradient value.stops
            return new ColorObj({
                type: "gradient",
                value: {
                    ...this.value,
                    stops: this.value.stops.map(stop => ({
                        ...stop,
                        color: tinycolor(stop.color)[action](value).toRgbString()
                    }))
                }
            });
        }
    }

    lighten(value) {
        return this._adjustColor("lighten", value);
    }

    darken(value) {
        return this._adjustColor("darken", value);
    }

    saturate(value) {
        return this._adjustColor("saturate", value);
    }

    desaturate(value) {
        return this._adjustColor("desaturate", value);
    }

    setAlpha(value) {
        return this._adjustColor("setAlpha", value);
    }

    getAlpha() {
        return tinycolor(this.value).getAlpha();
    }

    blend(color, opacity) {
        let blendedColor = blendColors(tinycolor(this.value).setAlpha(opacity), tinycolor(color.value));
        return new ColorObj({
            type: "color",
            value: blendedColor.toRgbString(),
            styleSet: blendedColor.isDark() ? "dark" : "light"
        });
    }
}

export function color(props) {
    return new ColorObj(props);
}

export function migrateLegacyColor(styleSet, color) {
    if (typeof color == "string") {
        if (color == "theme") {
            return "color-0";
        }
        if (color.startsWith("accent")) {
            return `color-${color.substring("accent".length)}`;
        }
        if (color.startsWith("chart") && !color.startsWith("chart-")) {
            return `chart-${parseInt(color.substring("chart".length))}`;
        }
        if (color == "background_accent") {
            return "container-0";
        }
        if (color == "background_light") {
            return app.currentTheme.getBackground("light").value;
        }
        if (color == "background_dark") {
            return app.currentTheme.getBackground("dark").value;
        }
        if (color == "colorful") {
            // legacy colorful
            if (styleSet.collectionColors.length > 0) {
                return "collection-0";
            } else {
                return "color-0";
            }
        }
    }
    return color;
}

export function getDefaultShapeStyle(type) {
    switch (type) {
        case "outlined":
            return {
                shapeStyleId: "outlined",
                fillOpacity: 0,
                strokeOpacity: 1,
                strokeWidth: 2,
                colorRule: "both",
                fillColor: null,
                strokeColor: null,
                iconColor: "primary",
                iconScale: 0.75
            };
        case "muted":
            return {
                shapeStyleId: "muted",
                fillOpacity: 0.3,
                strokeOpacity: 0,
                strokeWidth: 0,
                colorRule: "both",
                fillColor: null,
                strokeColor: null,
                iconColor: "primary",
                iconScale: 0.75
            };
        case "fillAndStroke":
            return {
                shapeStyleId: "fillAndStroke",
                fillOpacity: 0.3,
                strokeOpacity: 1,
                strokeWidth: 2,
                colorRule: "both",
                fillColor: null,
                strokeColor: null,
                iconColor: "primary",
                iconScale: 0.75
            };
        case "filled":
        default:
            return {
                shapeStyleId: "filled",
                fillOpacity: 1,
                strokeOpacity: 0,
                strokeWidth: 0,
                colorRule: "both",
                fillColor: null,
                strokeColor: null,
                iconColor: "primary",
                iconScale: 0.75
            };
    }
}

export function getShadowFilter(shadow) {
    if (!shadow) return null;

    if (shadow.opacity != null) {
        // legacy shadow with separate color and opacity props
        const shadowColor = tinycolor(shadow.color).setAlpha(shadow.opacity);
        return `drop-shadow(${shadow.x}px ${shadow.y}px ${shadow.blur}px ${shadowColor.toRgbString()})`;
    }
    return `drop-shadow(${shadow.x}px ${shadow.y}px ${shadow.blur}px ${shadow.color})`;
}

export function getAutoStyleSetForBackground(background) {
    if (tinycolor(background.value).isDark()) {
        return "dark";
    } else {
        return "light";
    }
}

export function getShadedColor(baseColor, shadeColor, index, maxCount) {
    if (maxCount <= 1) {
        return baseColor;
    }
    let min = 0;
    let mult = 1;

    maxCount -= 1;

    if (shadeColor == "dark") {
        min = 0;
        mult = 0.5;
        shadeColor = "black";
    }
    if (shadeColor == "light") {
        min = 0;
        mult = 0.5;
        shadeColor = "white";
    }

    const shadedColor = blendColors(tinycolor(shadeColor).setAlpha(min + index / maxCount * mult), tinycolor(baseColor));
    return color({
        type: "color",
        value: shadedColor.toRgbString(),
        styleSet: shadedColor.isDark() ? "dark" : "light"
    });
}
